import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { IPatientListFilter } from '@common/contracts/filter/patient-list-filter';
import { Platform } from '@ionic/angular';
import moment from 'moment';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LastVisitChoice } from 'src/app/patient/components/patient-list/patient-list-filter/patient-list-filter.component';

@Component({
  selector: 'itl-search-filter-bar',
  templateUrl: './search-filter-bar.component.html',
  styleUrls: ['./search-filter-bar.component.scss'],
})
export class SearchFilterBarComponent implements OnInit, OnDestroy {
  @Input() public filterActive: boolean;
  @Input() public showFilter = true;
  @Input() public showBoxShadow = true;
  @Input() public instantSearch = false;
  @Input() public placeholder = 'Suche';
  @Input() public dateField: string;
  @Input() public value: string;
  @Input() public disabled: boolean;
  @Input() public enablePhoneFilter? = false;
  @Input() public patientFilters?: IPatientListFilter;
  @Output() public valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public filterTypePressed: EventEmitter<null> = new EventEmitter<null>();
  @Output() public searchValueChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() public searchValueReset: EventEmitter<null> = new EventEmitter<null>();
  @Output() public resetFilter: EventEmitter<void> = new EventEmitter<void>();
  @Output() public dismissFilter = new EventEmitter<string>();
  public LastVisitChoice = LastVisitChoice;
  public filterIcons = {
    fieldNurseId: 'alberta-fieldnurse',
    nursingHomeId: 'alberta-filter-nursinghome',
    nursingServiceId: 'alberta-nursingService',
    subunitId: 'alberta-subunit',
  };

  @ViewChild('searchInput', { static: true }) searchElement: ElementRef;

  private _search$$: Subscription;

  constructor(private _platform: Platform) {}
  ngOnDestroy(): void {
    if (this._search$$) {
      this._search$$.unsubscribe();
    }
  }

  ngOnInit() {
    if (!this.instantSearch) {
      return;
    }

    const inputElement = this.searchElement.nativeElement;

    fromEvent(inputElement, 'keyup')
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.search());
  }

  public search(): void {
    this.valueChange.emit(this.value);
    this.searchValueChanged.emit(this.toSearchTerm(this.value));
  }

  public filterType(): void {
    this.filterTypePressed.emit();
  }

  public get activeFilterArgs(): string[] {
    if (!this.patientFilters?.filterArgs) {
      return [];
    }

    return Object.entries(this.patientFilters.filterArgs)
      .filter(([_, value]) => value != null)
      .map(([key]) => key);
  }

  public get hasAnySpecialFilters(): boolean {
    return !!(this.patientFilters?.therapyId || this.patientFilters?.payerType || this.patientFilters?.lastVisit);
  }

  public get totalFilterCount(): number {
    let count = this.activeFilterArgs.length;
    if (this.patientFilters?.lastVisit !== LastVisitChoice.anyTime) {
      count++;
    }
    if (this.patientFilters?.therapyId != null) {
      count++;
    }
    if (this.patientFilters?.payerType != null) {
      count++;
    }
    return count;
  }

  // This function evaluates the maximum amount of visible filters to appear on the searchbar
  // based on the resolution. A small screen should only show a maximum of 1 active filter,
  // whereas a large screen can show up to 3 active fitlers.
  public get maxVisibleFilters(): number {
    const width = window.innerWidth;
    const smallScreenBreakPoint = 768;
    const mediumScreenBreakPoint = 1024;

    // Small screen = show only one filter
    if (width <= smallScreenBreakPoint) {
      return 1;
    }
    // Medium screen = show two filters
    if (width <= mediumScreenBreakPoint) {
      return 2;
    }
    // Large screen = show three filters
    return 3;
  }

  public get shouldShowCompactFilters(): boolean {
    return this.totalFilterCount > this.maxVisibleFilters;
  }

  private toSearchTerm(query: string): string {
    if (query == null) {
      return '';
    }
    // Matches German phone numbers in local (0...)
    // or international format (+49... / 0049...).
    // Spaces, hyphens and parentheses are allowed
    const germanPhoneNumberPattern = /^(0049|\+49|0)[\d\s\-()]+$/;

    // Phone Filter
    if (germanPhoneNumberPattern.test(query) && this.enablePhoneFilter) {
      let normalizedPhone = query.replace(/[-\s()]/g, '');
      if (normalizedPhone.startsWith('0049')) {
        normalizedPhone = normalizedPhone.substring(4);
      } else if (normalizedPhone.startsWith('+49')) {
        normalizedPhone = normalizedPhone.substring(3);
      } else if (normalizedPhone.startsWith('0')) {
        normalizedPhone = normalizedPhone.substring(1);
      }
      return normalizedPhone;
    }
    // Birthday Filter
    return query.split(' ').reduce((term, word) => {
      if (word.trim()) {
        if (moment(word, 'DD.MM.YYYY', true).isValid()) {
          word = moment(word, 'DD.MM.YYYY', true).format('YYYY-MM-DD');
          if (this.dateField != null && this.dateField !== '') {
            word = `+${this.dateField}:${!this._platform.is('hybrid') ? word.replace(/[^a-z0-9]/gi, '') : word}`;
          }
        }

        term = `${term}${term.length > 0 ? ' ' : ''}${word}`;
      }
      return term;
    }, '');
  }

  resetSearch() {
    this.value = '';
    this.valueChange.emit(this.value);
    this.searchValueReset.emit();
  }

  resetFilterclicked() {
    this.resetFilter.emit();
  }
}
